import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithCredential,
    signInWithPhoneNumber,
    RecaptchaVerifier
} from 'firebase/auth';
import axios from 'axios';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    function signup(email, password, name) {
        return createUserWithEmailAndPassword(auth, email, password)
            .then((userCredential) => {
                return updateProfile(userCredential.user, { displayName: name })
                    .then(() => userCredential);
            });
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    async function signInWithGoogle() {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        const apiUrl = process.env.REACT_APP_API_URL;

        try {
            await axios.get(`${apiUrl}/api/users/profile/${user.uid}`);
        } catch (err) {
            if (err.response?.status === 404) {
                const res = await axios.post(`${apiUrl}/api/users/register`, {
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName || '',
                });
                setUserRole(res.data.role);
            }
        }
        return result;
    }

    async function signInWithGoogleCredential(idToken) {
        const credential = GoogleAuthProvider.credential(idToken);
        const result = await signInWithCredential(auth, credential);
        const user = result.user;
        const apiUrl = process.env.REACT_APP_API_URL;
        try {
            await axios.get(`${apiUrl}/api/users/profile/${user.uid}`);
        } catch (err) {
            if (err.response?.status === 404) {
                const res = await axios.post(`${apiUrl}/api/users/register`, {
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName || '',
                });
                setUserRole(res.data.role);
            }
        }
        return result;
    }

    function setupRecaptcha(containerId) {
        if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch (e) {}
        }
        window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
            size: 'invisible',
        });
        return window.recaptchaVerifier;
    }

    function sendPhoneOTP(phoneNumber) {
        return signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
    }

    const fetchAndSetRole = async (uid) => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const res = await axios.get(`${apiUrl}/api/users/profile/${uid}`);
            setUserRole(res.data.role || 'student');
        } catch (error) {
            if (error.response?.status === 404) {
                console.log('[AuthContext] Profile not found yet.');
            } else {
                setUserRole('student');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                await fetchAndSetRole(user.uid);
            } else {
                setUserRole(null);
                setLoading(false);
            }
        });
        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userRole,
        isAdmin: userRole === 'admin',
        loading,
        signup,
        login,
        logout,
        signInWithGoogle,
        signInWithGoogleCredential,
        setupRecaptcha,
        sendPhoneOTP,
        setUserRole
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
